"use client";

import ButtonWithIcon from "@/components/atoms/button-with-icon";
import RadioButton from "@/components/atoms/radio-buttons";
import RadioShipping from "@/components/atoms/radio-shipping";
import AddressLoader from "@/components/modules/account/address-loader";
import ModalTemplate from "@/components/molecules/modal-template";
import EmptyAddressCard from "@/components/organisms/empty-address-card";
import { Toast } from "@/components/atoms/toast";
import BillingAddressForm from "@/components/organisms/forms/checkout/billing-address-form";
import ShippingAddressForm from "@/components/organisms/forms/checkout/shipping-address-form";
import BillingAddressCard from "@/components/templates/Checkout/components/billing-address-card";
import ShippingAddressCard from "@/components/templates/Checkout/components/shipping-address-card";
import { Form, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useCustomer } from "@/hooks/useCustomer";
import { Locales } from "@/lib/i18n";
import { __ } from "@/lib/i18n/functions";
import { cn, getDifferentAddress } from "@/lib/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { useCheckoutStore } from "@/stores/checkout-store";
import { useAllEmployees, useCompanyAddresses, useCompanyDetails } from "@/queries/company";

type AddressClientProps = {
  locale: Locales;
};

const accountTypeSchema = z.object({
  accountType: z.enum(["individual", "company"]),
});

const AddressClient = ({ locale }: AddressClientProps) => {
  const { customer } = useCustomer();
  const [isAddShippingAddressModalOpen, setIsAddShippingAddressModalOpen] = useState(false);
  const [isBillingAddressModalOpen, setisBillingAddressModalOpen] = useState(false);

  const accountType = useCheckoutStore((state) => state.accountType);
  const setData = useCheckoutStore((state) => state.setData);

  const loading = customer === undefined;

  const { shippingAddress: customerShippingAddress, billingAddress } = getDifferentAddress(customer?.addresses);
  const { data: companyDetails, isLoading: isCompanyLoading } = useCompanyDetails(
    customer?.company_name || "",
    customer?.id || "anonymous"
  );
  const { data: employees } = useAllEmployees(companyDetails?.id || "");
  const { data: companyAddresses, isLoading: isCompanyAddressesLoading } = useCompanyAddresses(
    accountType === "company" || customer?.company_name ? companyDetails?.id : ""
  );
  const { shippingAddress: companyShippingAddress } = getDifferentAddress(companyAddresses);

  const form = useForm<z.infer<typeof accountTypeSchema>>({
    resolver: zodResolver(accountTypeSchema),
    defaultValues: {
      accountType: accountType || "individual",
    },
  });

  const { control, watch, setValue } = form;

  const accountTypeValue = watch("accountType");
  const isCompanyAccountSelected = accountTypeValue === "company";
  const shippingAddress = isCompanyAccountSelected ? companyShippingAddress : customerShippingAddress;
  const currentEmployee = employees
    ?.filter((employee) => employee.customer)
    .find((employee) => employee.customer.id === customer?.id);
  const canManageCompanyAddresses =
    !isCompanyAccountSelected || customer?.id === companyDetails?.created_by || currentEmployee?.is_admin;
  const addressSource = isCompanyAccountSelected ? "company" : "customer";

  useEffect(() => {
    if (accountTypeValue && accountTypeValue !== accountType) {
      setData({ accountType: accountTypeValue });
    }
  }, [accountTypeValue, setData, accountType]);

  useEffect(() => {
    if (!accountType && customer?.company_name) {
      setValue("accountType", "company");
      setData({ accountType: "company" });
    }
  }, [accountType, customer?.company_name, setData, setValue]);

  const filteredBillingAddress = billingAddress?.filter((address) => {
    if (accountTypeValue === "company") {
      return true;
    } else {
      return !address.company || address.company.trim() === "";
    }
  });

  return (
    <article className={cn("text-dark-primary flex-1/2 space-y-10 lg:pb-7.5")}>
      {loading ? (
        <>
          <section className="space-y-5">
            <header className="flex flex-wrap items-center justify-between gap-2.5 border-b border-black/19 pb-5">
              <h2 className="font-cabinet-grotesk text-2xl font-medium md:text-3xl">
                {__("Delivery addresses", locale)}
              </h2>
            </header>
            {[1, 2].map((i) => (
              <AddressLoader key={`shipping-skeleton-${i}`} />
            ))}
          </section>

          <section className="space-y-5">
            <header className="flex flex-wrap items-center justify-between gap-2.5 border-b border-black/19 pb-5">
              <h2 className="font-cabinet-grotesk text-2xl font-medium md:text-3xl">
                {__("Billing addresses", locale)}
              </h2>
            </header>
            {[1, 2].map((i) => (
              <AddressLoader key={`billing-skeleton-${i}`} />
            ))}
          </section>
        </>
      ) : (
        <>
          <section className="space-y-5">
            <header className="flex flex-wrap items-center justify-between gap-2.5 border-b border-black/19 pb-5">
              <h2 className="font-cabinet-grotesk text-2xl font-medium md:text-3xl">
                {__("Delivery addresses", locale)}
              </h2>
              {canManageCompanyAddresses && (
                <ModalTemplate
                  open={isAddShippingAddressModalOpen}
                  onOpenChange={setIsAddShippingAddressModalOpen}
                  classNameTitle="font-cabinet-grotesk leading-h1 text-lg md:text-1xl"
                  title={__("Add a delivery address", locale)}
                  triggerAsChild
                  modalTrigger={
                    <ButtonWithIcon
                      onClick={() => setIsAddShippingAddressModalOpen(true)}
                      variant="rounded"
                      size="md"
                      className="h-10 text-sm"
                      iconLeftName="Plus"
                    >
                      {__("Add an address", locale)}
                    </ButtonWithIcon>
                  }
                >
                  <ShippingAddressForm
                    locale={locale}
                    onSuccess={() => setIsAddShippingAddressModalOpen(false)}
                    addressSource={addressSource}
                    companyId={companyDetails?.id}
                  />
                </ModalTemplate>
              )}
            </header>
            {isCompanyAccountSelected && !isCompanyLoading && !companyDetails?.id ? (
              <Toast
                id="company-addresses-missing-company-account"
                type="info"
                title={__("Company not found", locale)}
                className="max-w-none shadow-none"
              />
            ) : isCompanyAccountSelected && (isCompanyLoading || isCompanyAddressesLoading) ? (
              <AddressLoader />
            ) : shippingAddress && shippingAddress.length > 0 ? (
              shippingAddress.map((address) => (
                <ShippingAddressCard
                  type="account"
                  address={address}
                  key={address.id}
                  locale={locale}
                  addressSource={addressSource}
                  companyId={companyDetails?.id}
                  canManage={canManageCompanyAddresses}
                />
              ))
            ) : !canManageCompanyAddresses ? (
              <Toast
                id="company-addresses-readonly-account"
                type="info"
                title={__("No company delivery address is available. Ask a company admin to add one.", locale)}
                className="max-w-none shadow-none"
              />
            ) : (
              <EmptyAddressCard locale={locale} modalTitle={__("Add a shipping address", locale)}>
                <ShippingAddressForm
                  locale={locale}
                  onSuccess={() => setIsAddShippingAddressModalOpen(false)}
                  addressSource={addressSource}
                  companyId={companyDetails?.id}
                />
              </EmptyAddressCard>
            )}
          </section>
          <section className="space-y-5">
            <hr className="border-primary-light/50" />
            <Form {...form}>
              <form>
                <FormField
                  control={control}
                  name="accountType"
                  render={({ field }) => (
                    <FormItem className="w-full gap-1">
                      <div className="flex flex-col gap-2.5">
                        <FormLabel className="mb-0 text-base font-medium">
                          {__("Account Type", locale)} <span className="text-pink-primary">*</span>
                        </FormLabel>
                        <RadioShipping
                          locale={locale}
                          name="acc-type"
                          onValueChange={field.onChange}
                          defaultValue={field.value}
                          className="max-w-none flex-row gap-4 max-sm:hidden lg:flex-col xl:flex-row"
                          items={[
                            { value: "company", content: { title: __("I am a business", locale) } },
                            { value: "individual", content: { title: __("I am an individual", locale) } },
                          ]}
                        />
                        <RadioButton
                          name="account-type-mobile"
                          onValueChange={field.onChange}
                          defaultValue={field.value}
                          className="w-full flex-col items-start gap-2.5 min-sm:hidden"
                          options={[
                            { value: "company", label: __("I am a business", locale) },
                            { value: "individual", label: __("I am an individual", locale) },
                          ]}
                        />
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </form>
            </Form>
            <hr className="border-primary-light/50" />
          </section>
          <section className="space-y-5">
            <header className="flex flex-wrap items-center justify-between gap-2.5 border-b border-black/19 pb-5">
              <h2 className="font-cabinet-grotesk text-2xl font-medium md:text-3xl">
                {__("Billing addresses", locale)}
              </h2>
              <ModalTemplate
                open={isBillingAddressModalOpen}
                onOpenChange={setisBillingAddressModalOpen}
                className="sm:max-w-[920px]"
                classNameTitle="font-cabinet-grotesk leading-h1 text-lg md:text-1xl"
                title={__("Add a billing address", locale)}
                triggerAsChild
                modalTrigger={
                  <ButtonWithIcon variant="rounded" size="md" className="h-10 text-sm" iconLeftName="Plus">
                    {__("Add an address", locale)}
                  </ButtonWithIcon>
                }
              >
                <BillingAddressForm
                  accountTypeValue={accountTypeValue}
                  locale={locale}
                  onSuccess={() => setisBillingAddressModalOpen(false)}
                />
              </ModalTemplate>
            </header>
            {filteredBillingAddress && filteredBillingAddress.length > 0 ? (
              filteredBillingAddress.map((address) => (
                <BillingAddressCard type="account" address={address} key={address.id} locale={locale} />
              ))
            ) : (
              <EmptyAddressCard
                locale={locale}
                modalTitle={__("Add a billing address", locale)}
                modalClassName="sm:max-w-[920px]"
              >
                <BillingAddressForm
                  accountTypeValue={accountTypeValue}
                  locale={locale}
                  onSuccess={() => setisBillingAddressModalOpen(false)}
                />
              </EmptyAddressCard>
            )}
          </section>
        </>
      )}
    </article>
  );
};

export default AddressClient;
